import { differenceInDays, subDays } from 'date-fns';

import { QueryConditionsDto } from '~/__generated__/query-service';
import { useDashboardQueryServiceContext } from '~/containers/Dashboard/Provider/DasboardQueryServiceProvider';
import { useDashboardContext } from '~/containers/Dashboard/Provider/DashboardProvider';
import { useQueryServiceApi } from '~/hooks/useQueryServiceApi';
import {
  QueryServiceMetricsConfig,
  QueryServiceMetricsConfigKey,
} from '~/types/query-service-helpers';

type Params = {
  metricId: QueryServiceMetricsConfigKey;
};

export const usePersonalDashboardOverviewMetricData = ({
  metricId,
}: Params) => {
  const { filters } = useDashboardContext();
  const { conditions } = useDashboardQueryServiceContext();

  const { isLoading, data } = useQueryServiceApi(
    ['dashboard', conditions, metricId],
    (Api) =>
      Api.query.query({
        requestBody: {
          conditions: conditions!,
          projection: [QueryServiceMetricsConfig[metricId]],
        },
      }),
    {
      enabled: Boolean(metricId) && Boolean(conditions),
    }
  );

  const { isLoading: isComparedLoading, data: comparedData } =
    useQueryServiceApi(
      ['dashboard', 'compare', conditions, metricId],
      (Api) => {
        let lenDays = differenceInDays(
          new Date(filters.endTime),
          new Date(filters.startTime)
        );

        if (lenDays <= 0) {
          lenDays = 1;
        }

        const compareConditions = {
          ...conditions,
          datetime_range: {
            ...conditions?.datetime_range,
            from: subDays(new Date(filters.startTime), lenDays).toISOString(),
            until: subDays(new Date(filters.endTime), lenDays).toISOString(),
          },
        } satisfies QueryConditionsDto;

        return Api.query.query({
          requestBody: {
            conditions: compareConditions!,
            projection: [QueryServiceMetricsConfig[metricId]],
          },
        });
      },
      {
        enabled: Boolean(metricId) && Boolean(conditions) && filters.compare,
      }
    );

  return {
    loading: isLoading || isComparedLoading,
    data,
    comparedData,
    compare: filters.compare,
  };
};
